import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import * as tf from '@tensorflow/tfjs';
import { lastValueFrom } from 'rxjs';
import { RoboflowService } from 'src/app/services/roboflow.service';

interface Detection {
  class: string;
  confidence: number;
  bbox: number[];
  keypoints: number[][]; // Array de [x, y, confidence] para cada keypoint
  midpoint?: number[]; // Punto medio entre punto 2 y 3
  angle?: number; // Ángulo entre punto 1, midpoint y último punto
}

interface MedialArchResponse {
  confidence: number,
  image: {
    width: number,
    height: number
  },
  inference_id: string,
  predictions: {
    class: string,
    class_id: number,
    confidence: number
  }[],
  time: number,
  top: string // La predicción con más confianza
}

@Component({
  selector: 'app-yolo-detector',
  templateUrl: './yolo-detector.component.html',
  styleUrls: ['./yolo-detector.component.scss']
})
export class YoloDetectorComponent implements OnInit {
  @ViewChild('outputCanvas', { static: false }) outputCanvas!: ElementRef<HTMLCanvasElement>;

  modelLoaded = false;
  modelInfo: any = null;
  selectedImage: HTMLImageElement | null = null;
  detectionResults: Detection[] = [];
  loading = false;
  error: string = '';
  editMode = true;
  selectedDetectionIndex: number = 0;
  draggingKeypointIndex: number | null = null;
  anglesCorrected = false;
  medialArchResult: string | null = null
  
  private model: tf.GraphModel | null = null;
  private readonly MODEL_PATH = 'assets/models/weights_web_model/model.json';
  private readonly INPUT_SIZE = 640;
  
  private readonly COCO_CLASSES = [
    'left_alignment', 'right_alignment'
  ];

  constructor(private roboflowService: RoboflowService) { }

  async ngOnInit() {
    await this.loadModel();
  }

  async sendMedialArchImage() {
    const response: MedialArchResponse = await lastValueFrom(this.roboflowService.sendMedialArchImage("https://wikyscan-2021-dev.s3.eu-west-1.amazonaws.com/medial_arch.png"))
    console.log("response: ", response)
    this.medialArchResult = response.top
  }

  async loadModel() {
    this.loading = true;
    this.error = '';
    
    try {
      console.log('Cargando modelo YOLO desde:', this.MODEL_PATH);
      this.model = await tf.loadGraphModel(this.MODEL_PATH);
      this.modelLoaded = true;
      
      this.modelInfo = {
        inputs: this.model.inputs.map((input: any) => ({
          name: input.name,
          shape: input.shape
        })),
        outputs: this.model.outputs.map((output: any) => ({
          name: output.name,
          shape: output.shape
        }))
      };
      
      console.log('Modelo YOLO cargado exitosamente');
      console.log('Información del modelo:', this.modelInfo);
    } catch (error: any) {
      this.error = 'Error al cargar el modelo: ' + error.message;
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  onImageSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.loadImageFromFile(file);
    }
  }

  loadImageFromFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const img = new Image();
      img.onload = () => {
        this.selectedImage = img;
        setTimeout(() => {
          this.runDetection(img);
        }, 0);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  async loadSampleImage() {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this.selectedImage = img;
      setTimeout(() => {
        this.runDetection(img);
      }, 0);
    };
    img.src = 'https://ultralytics.com/images/bus.jpg';
  }

  async runDetection(img: HTMLImageElement) {
    if (!this.model) {
      this.error = 'El modelo no está cargado';
      return;
    }

    this.loading = true;
    this.error = '';
    this.detectionResults = [];

    try {
      const inputTensor = await this.preprocessImage(img);
      
      console.log('Input tensor shape:', inputTensor.shape);
      
      const predictions = this.model.predict(inputTensor);
      
      let outputTensor: tf.Tensor;
      
      if (Array.isArray(predictions)) {
        console.log('Model returned', predictions.length, 'outputs');
        outputTensor = predictions[0];
        console.log('Using first output, shape:', outputTensor.shape);
      } else {
        outputTensor = predictions as tf.Tensor;
        console.log('Single output, shape:', outputTensor.shape);
      }
      
      const detections = await this.processDetections(outputTensor, img.width, img.height);
      this.detectionResults = detections;
      
      this.drawDetections(img, detections);
      
      inputTensor.dispose();
      if (Array.isArray(predictions)) {
        predictions.forEach((t: tf.Tensor) => t.dispose());
      } else {
        (predictions as tf.Tensor).dispose();
      }
      
    } catch (error: any) {
      this.error = 'Error al realizar la detección: ' + error.message;
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  private async preprocessImage(img: HTMLImageElement): Promise<tf.Tensor> {
    return tf.tidy(() => {
      let tensor = tf.browser.fromPixels(img);
      tensor = tf.image.resizeBilinear(tensor, [this.INPUT_SIZE, this.INPUT_SIZE]);
      tensor = tensor.div(255.0);
      tensor = tensor.expandDims(0);
      return tensor;
    });
  }

  private async processDetections(predictions: tf.Tensor, imgWidth: number, imgHeight: number): Promise<Detection[]> {
    const predArray = await predictions.array() as any;
    const detections: Detection[] = [];
    
    const confidenceThreshold = 0.25;
    const iouThreshold = 0.45;
    const keypointThreshold = 0.5;
    
    // Calcular factores de escala del modelo (640x640) a la imagen original
    const scaleX = imgWidth / this.INPUT_SIZE;
    const scaleY = imgHeight / this.INPUT_SIZE;
    
    console.log('Image dimensions:', imgWidth, 'x', imgHeight);
    console.log('Scale factors:', scaleX, scaleY);
    
    let boxes: any[];
    
    if (Array.isArray(predArray) && predArray.length > 0 && Array.isArray(predArray[0])) {
      boxes = predArray[0];
      console.log('Processing', boxes.length, 'boxes with keypoints');
    } else {
      console.error('Formato de predicción inválido');
      return [];
    }
    
    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      
      if (!Array.isArray(box) || box.length < 6) {
        continue;
      }
      
      // Las coordenadas del bbox ya están en el espacio de la imagen original
      const x1 = Number(box[0]);
      const y1 = Number(box[1]);
      const x2 = Number(box[2]);
      const y2 = Number(box[3]);
      const confidence = Number(box[4]);
      const classId = Math.round(Number(box[5]));
      
      if (confidence > confidenceThreshold) {
        // IMPORTANTE: Las coordenadas del bbox TAMBIÉN vienen en coordenadas del modelo (0-640)
        // Necesitamos escalarlas a las coordenadas de la imagen original
        const scaledX1 = x1 * scaleX;
        const scaledY1 = y1 * scaleY;
        const scaledX2 = x2 * scaleX;
        const scaledY2 = y2 * scaleY;
        
        // Extraer keypoints (cada keypoint tiene x, y, confidence)
        const keypoints: number[][] = [];
        const numKeypoints = Math.floor((box.length - 6) / 3);
        
        for (let j = 0; j < numKeypoints; j++) {
          const kpIndex = 6 + (j * 3);
          if (kpIndex + 2 < box.length) {
            const kpX = Number(box[kpIndex]);
            const kpY = Number(box[kpIndex + 1]);
            const kpConf = Number(box[kpIndex + 2]);
            
            // Solo agregar keypoints con confianza suficiente
            if (kpConf > keypointThreshold) {
              // Los keypoints vienen en coordenadas del modelo (0-640)
              // Escalarlos a las coordenadas de la imagen original
              const scaledKpX = kpX * scaleX;
              const scaledKpY = kpY * scaleY;
              
              keypoints.push([scaledKpX, scaledKpY, kpConf]);
            }
          }
        }
        
        if (keypoints.length > 0) {
          console.log(`Detection ${i}: ${keypoints.length} keypoints, sample (scaled):`, keypoints[0]);
        }
        
        // Calcular punto medio y ángulo si hay suficientes keypoints
        if (keypoints.length >= 4) {
          const midpoint = this.calculateMidpoint(keypoints[1], keypoints[2]);
          const angle = this.calculateAngleThreePoints(keypoints[0], midpoint, keypoints[3]);
          
          console.log(`Midpoint between KP2 and KP3:`, midpoint);
          console.log(`Angle KP1 → Midpoint → KP4: ${angle.toFixed(2)}°`);
          
          detections.push({
            class: this.COCO_CLASSES[classId] || `Class ${classId}`,
            confidence: confidence,
            bbox: [
              Math.max(0, scaledX1),
              Math.max(0, scaledY1),
              Math.min(imgWidth, scaledX2),
              Math.min(imgHeight, scaledY2)
            ],
            keypoints: keypoints,
            midpoint: midpoint,
            angle: angle
          });
        } else {
          detections.push({
            class: this.COCO_CLASSES[classId] || `Class ${classId}`,
            confidence: confidence,
            bbox: [
              Math.max(0, scaledX1),
              Math.max(0, scaledY1),
              Math.min(imgWidth, scaledX2),
              Math.min(imgHeight, scaledY2)
            ],
            keypoints: keypoints
          });
        }
      }
    }
    
    console.log('Found', detections.length, 'detections before NMS');
    
    const finalDetections = this.applyNMS(detections, iouThreshold);
    console.log('Final detections after NMS:', finalDetections.length);
    
    return finalDetections;
  }

  private applyNMS(detections: Detection[], iouThreshold: number): Detection[] {
    detections.sort((a, b) => b.confidence - a.confidence);
    
    const selected: Detection[] = [];
    
    for (const detection of detections) {
      let shouldSelect = true;
      
      for (const selectedDetection of selected) {
        const iou = this.calculateIoU(detection.bbox, selectedDetection.bbox);
        if (iou > iouThreshold) {
          shouldSelect = false;
          break;
        }
      }
      
      if (shouldSelect) {
        selected.push(detection);
      }
    }
    
    return selected;
  }

  private calculateIoU(box1: number[], box2: number[]): number {
    const x1 = Math.max(box1[0], box2[0]);
    const y1 = Math.max(box1[1], box2[1]);
    const x2 = Math.min(box1[2], box2[2]);
    const y2 = Math.min(box1[3], box2[3]);
    
    const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const area1 = (box1[2] - box1[0]) * (box1[3] - box1[1]);
    const area2 = (box2[2] - box2[0]) * (box2[3] - box2[1]);
    const union = area1 + area2 - intersection;
    
    return intersection / union;
  }

  private drawDetections(img: HTMLImageElement, detections: Detection[]) {
      if (!this.outputCanvas) {
        console.error('Output canvas not available yet');
        return;
      }

      const canvas = this.outputCanvas.nativeElement;
      const ctx = canvas.getContext('2d')!;

      // Limitar el tamaño del canvas para visualización
      const maxWidth = 800;
      const scale = Math.min(1, maxWidth / img.width);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      // Dibujar la imagen escalada
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      console.log('Canvas size:', canvas.width, 'x', canvas.height);
      console.log('Image size:', img.width, 'x', img.height);
      console.log('Display scale:', scale);

      detections.forEach((detection, index) => {
        const colors = ['#4169E1', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F'];
        const color = colors[index % colors.length];

        // Dibujar keypoints y conectarlos con líneas
        if (detection.keypoints && detection.keypoints.length > 0) {
          const isSelected = this.editMode && this.selectedDetectionIndex === index;

          if (!this.anglesCorrected) {
            // ANTES DE CORRECCIÓN: Mostrar todos los keypoints conectados con líneas
            ctx.strokeStyle = '#4169E1';
            ctx.lineWidth = 3;
            ctx.beginPath();

            detection.keypoints.forEach((kp, kpIndex) => {
              const kpX = kp[0] * scale;
              const kpY = kp[1] * scale;

              if (kpIndex === 0) {
                ctx.moveTo(kpX, kpY);
              } else {
                ctx.lineTo(kpX, kpY);
              }
            });

            ctx.stroke();

            // Dibujar círculos en cada keypoint (más grandes, sin números)
            detection.keypoints.forEach((kp, kpIndex) => {
              const kpX = kp[0] * scale;
              const kpY = kp[1] * scale;

              ctx.fillStyle = '#4169E1';
              ctx.beginPath();
              ctx.arc(kpX, kpY, 10, 0, 2 * Math.PI);
              ctx.fill();

              ctx.strokeStyle = 'white';
              ctx.lineWidth = 2;
              ctx.stroke();
            });
          } else {
            // DESPUÉS DE CORRECCIÓN: Solo mostrar KP1, midpoint y KP4
            if (detection.midpoint) {
              const midX = detection.midpoint[0] * scale;
              const midY = detection.midpoint[1] * scale;

              // Línea del punto 1 al midpoint
              ctx.strokeStyle = '#4CAF50';
              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.moveTo(detection.keypoints[0][0] * scale, detection.keypoints[0][1] * scale);
              ctx.lineTo(midX, midY);
              ctx.stroke();

              // Línea del midpoint al punto 4
              ctx.strokeStyle = '#4CAF50';
              ctx.lineWidth = 4;
              ctx.beginPath();
              ctx.moveTo(midX, midY);
              ctx.lineTo(detection.keypoints[3][0] * scale, detection.keypoints[3][1] * scale);
              ctx.stroke();

              // Dibujar KP1 (verde)
              const kp1X = detection.keypoints[0][0] * scale;
              const kp1Y = detection.keypoints[0][1] * scale;
              ctx.fillStyle = '#4CAF50';
              ctx.beginPath();
              ctx.arc(kp1X, kp1Y, 12, 0, 2 * Math.PI);
              ctx.fill();
              ctx.strokeStyle = 'white';
              ctx.lineWidth = 2;
              ctx.stroke();

              // Dibujar el punto medio (verde)
              ctx.fillStyle = '#4CAF50';
              ctx.beginPath();
              ctx.arc(midX, midY, 12, 0, 2 * Math.PI);
              ctx.fill();
              ctx.strokeStyle = 'white';
              ctx.lineWidth = 2;
              ctx.stroke();

              // Dibujar KP4 (verde)
              const kp4X = detection.keypoints[3][0] * scale;
              const kp4Y = detection.keypoints[3][1] * scale;
              ctx.fillStyle = '#4CAF50';
              ctx.beginPath();
              ctx.arc(kp4X, kp4Y, 12, 0, 2 * Math.PI);
              ctx.fill();
              ctx.strokeStyle = 'white';
              ctx.lineWidth = 2;
              ctx.stroke();
              
              // Dibujar el ángulo en el punto medio
              if (detection.angle !== undefined) {
                ctx.font = 'bold 24px Arial';
                ctx.fillStyle = '#4CAF50';
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 5;
                const angleText = `${detection.angle.toFixed(1)}°`;
                ctx.strokeText(angleText, midX + 20, midY - 20);
                ctx.fillText(angleText, midX + 20, midY - 20);
              }
            }
          }
        }
      });
    }


  private calculateMidpoint(point1: number[], point2: number[]): number[] {
    const midX = (point1[0] + point2[0]) / 2;
    const midY = (point1[1] + point2[1]) / 2;
    return [midX, midY];
  }

  private calculateAngleThreePoints(point1: number[], midpoint: number[], point3: number[]): number {
    // Calcular el ángulo en el vértice (midpoint) formado por los tres puntos
    // Vector 1: desde midpoint hasta point1
    // Vector 2: desde midpoint hasta point3
    
    const vector1X = point1[0] - midpoint[0];
    const vector1Y = point1[1] - midpoint[1];
    
    const vector2X = point3[0] - midpoint[0];
    const vector2Y = point3[1] - midpoint[1];
    
    // Calcular el ángulo de cada vector
    const angle1 = Math.atan2(vector1Y, vector1X);
    const angle2 = Math.atan2(vector2Y, vector2X);
    
    // Calcular la diferencia de ángulos
    let angleDiff = angle2 - angle1;
    
    // Convertir a grados
    angleDiff = angleDiff * (180 / Math.PI);
    
    // Normalizar al rango -180 a 180
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;
    
    // Retornar el valor absoluto para obtener el ángulo interior
    return Math.abs(angleDiff);
  }

  enableEditMode(detectionIndex: number) {
    this.editMode = true;
    this.selectedDetectionIndex = detectionIndex;
    this.redrawCanvas();
  }

  updateKeypoints() {
    // Recalcular punto medio y ángulo para todas las detecciones
    this.detectionResults.forEach(detection => {
      if (detection.keypoints.length >= 4) {
        detection.midpoint = this.calculateMidpoint(detection.keypoints[1], detection.keypoints[2]);
        detection.angle = this.calculateAngleThreePoints(
          detection.keypoints[0],
          detection.midpoint,
          detection.keypoints[3]
        );
        
        console.log('Updated midpoint:', detection.midpoint);
        console.log('Updated angle:', detection.angle);
      }
    });
    
    this.anglesCorrected = true;
    this.redrawCanvas();
  }

  exitEditMode() {
    this.editMode = false;
    this.selectedDetectionIndex = null;
    this.draggingKeypointIndex = null;
    this.redrawCanvas();
  }

  onCanvasMouseDown(event: MouseEvent) {
    if (!this.editMode) return;
    
    const canvas = this.outputCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    const scale = canvas.width / this.selectedImage!.width;
    
    // Buscar en todas las detecciones el keypoint más cercano
    let closestDistance = Infinity;
    let closestDetectionIndex = -1;
    let closestKeypointIndex = -1;
    
    this.detectionResults.forEach((detection, detIndex) => {
      for (let i = 0; i < detection.keypoints.length; i++) {
        const kpX = detection.keypoints[i][0] * scale;
        const kpY = detection.keypoints[i][1] * scale;
        
        const distance = Math.sqrt((x - kpX) ** 2 + (y - kpY) ** 2);
        
        if (distance < 15 && distance < closestDistance) {
          closestDistance = distance;
          closestDetectionIndex = detIndex;
          closestKeypointIndex = i;
        }
      }
    });
    
    if (closestDetectionIndex !== -1) {
      this.selectedDetectionIndex = closestDetectionIndex;
      this.draggingKeypointIndex = closestKeypointIndex;
    }
  }

  onCanvasMouseMove(event: MouseEvent) {
    if (!this.editMode || this.draggingKeypointIndex === null || this.selectedDetectionIndex === null) return;
    
    const canvas = this.outputCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Convertir coordenadas del canvas a coordenadas de la imagen
    const scale = canvas.width / this.selectedImage!.width;
    const imgX = x / scale;
    const imgY = y / scale;
    
    // Actualizar la posición del keypoint
    const detection = this.detectionResults[this.selectedDetectionIndex];
    detection.keypoints[this.draggingKeypointIndex][0] = imgX;
    detection.keypoints[this.draggingKeypointIndex][1] = imgY;
    
    this.redrawCanvas();
  }

  onCanvasMouseUp() {
    this.draggingKeypointIndex = null;
  }

  private redrawCanvas() {
    if (!this.selectedImage) return;
    this.drawDetections(this.selectedImage, this.detectionResults);
  }

  private calculateRelativeAngle(origin: number[], point1: number[], point2: number[]): number {
    const vector1X = point1[0] - origin[0];
    const vector1Y = point1[1] - origin[1];
    
    const vector2X = point2[0] - origin[0];
    const vector2Y = point2[1] - origin[1];
    
    const angle1 = Math.atan2(vector1Y, vector1X);
    const angle2 = Math.atan2(vector2Y, vector2X);
    
    let angleDiff = angle2 - angle1;
    angleDiff = angleDiff * (180 / Math.PI);
    
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;
    
    return angleDiff;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
